import { useCallback, useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BarChart3,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Users,
  MousePointerClick,
  Clock,
  Globe,
  Smartphone,
  LayoutPanelTop,
  UserPlus,
  Search,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";

/**
 * SUPERADMIN → Marketing → Analítica web (sep-2026).
 *
 * Google Analytics contado en claro: visitas, franjas, páginas y origen del
 * tráfico; y NUESTROS datos (registros, canales de reserva y uso del widget),
 * que no dependen de que el visitante acepte cookies.
 */

interface Overview {
  users: number;
  newUsers: number;
  sessions: number;
  pageViews: number;
  averageEngagementSeconds: number;
  engagedSessionRatePct: number;
}

interface WebAnalytics {
  days: number;
  overview: Overview;
  previousOverview: Overview;
  byDay: Array<{ date: string; users: number; sessions: number; pageViews: number }>;
  byHour: Array<{ hour: string; users: number; sessions: number }>;
  topPages: Array<{ path: string; title: string; views: number; users: number; averageEngagementSeconds: number }>;
  sources: Array<{ source: string; medium: string; campaign: string | null; users: number; sessions: number }>;
  devices: Array<{ device: string; users: number; sessions: number }>;
  countries: Array<{ country: string; users: number }>;
  landingPages: Array<{ path: string; sessions: number; users: number }>;
}

interface ProductAnalytics {
  days: number;
  signups: {
    total: number;
    byDay: Record<string, number>;
    byBookingMode: Record<string, number>;
    byCountry: Record<string, number>;
    onboardingCompleted: number;
    withBookings: number;
    payingNow: number;
    stillTrialing: number;
  };
  /** De dónde salieron los registros; "widget" son los del pie del widget. */
  signupOrigins: Array<{ origen: string; registros: number }>;
  signupsFromWidget: Array<{ negocio: string; registros: number }>;
  bookingsByDay: Array<{ date: string; reservations: number; appointments: number }>;
  channels: Array<{ channel: string; bookings: number }>;
  widget: {
    opens: number;
    bookings: number;
    conversionPct: number;
    byDay: Array<{ date: string; opens: number }>;
    topOrganizations: Array<{ organization: string; opens: number }>;
  };
}

interface SearchTotals {
  clicks: number;
  impressions: number;
  ctrPct: number;
  position: number;
}

interface SearchRow {
  value: string;
  clicks: number;
  impressions: number;
  ctrPct: number;
  position: number;
}

interface SearchPerformance {
  days: number;
  until: string;
  totals: SearchTotals;
  previousTotals: SearchTotals;
  byDay: Array<{ date: string; clicks: number; impressions: number; position: number }>;
  queries: SearchRow[];
  pages: SearchRow[];
  countries: SearchRow[];
  devices: SearchRow[];
  widget: { totals: SearchTotals; previousTotals: SearchTotals; pages: SearchRow[] };
}

interface Realtime {
  activeUsers: number;
  byMinute: Array<{ minutesAgo: number; users: number }>;
  topPages: Array<{ path: string; users: number }>;
  devices: Array<{ device: string; users: number }>;
}

const RANGES = [7, 14, 30, 90] as const;

const DEVICE_LABEL: Record<string, string> = { mobile: "Móvil", desktop: "Ordenador", tablet: "Tablet" };

function sourceLabel(source: string, medium: string): string {
  if (source === "(direct)") return "Directo (escriben la dirección o la tienen guardada)";
  if (medium === "organic") return `Búsqueda en ${source}`;
  if (medium === "cpc" || medium === "paid") return `Anuncios de ${source}`;
  if (medium === "referral") return `Enlace desde ${source}`;
  if (source === "(not set)") return "Sin identificar";
  return `${source} · ${medium}`;
}

function variation(current: number, previous: number): { text: string; className: string } {
  if (previous === 0) return { text: current > 0 ? "sin datos previos" : "—", className: "text-gray-400" };
  const pct = Math.round(((current - previous) / previous) * 1000) / 10;
  if (pct === 0) return { text: "igual que antes", className: "text-gray-400" };
  return {
    text: `${pct > 0 ? "+" : ""}${pct} % frente al periodo anterior`,
    className: pct > 0 ? "text-emerald-600" : "text-red-600",
  };
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

/** Dominio público de la web de ventas, que es lo que mide esta propiedad. */
const SITIO = "https://reservamanager.com";

/**
 * "/" no le dice nada a nadie (19-sep-2026): se pinta la dirección tal y como
 * se teclea, y se puede pinchar para abrir la página de verdad.
 */
function urlDePagina(path: string): string {
  return `${SITIO}${path.startsWith("/") ? path : `/${path}`}`;
}

function etiquetaDePagina(path: string): string {
  const limpio = path || "/";
  return limpio === "/" ? "reservamanager.com (portada)" : `reservamanager.com${limpio}`;
}

function shortDate(iso: string): string {
  const [, month, day] = iso.split("-");
  return day && month ? `${day}/${month}` : iso;
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
        {icon} {title}
      </h2>
      {children}
    </section>
  );
}

export default function WebAnalytics() {
  const [days, setDays] = useState<(typeof RANGES)[number]>(7);
  const [web, setWeb] = useState<WebAnalytics | null>(null);
  const [webError, setWebError] = useState<string | null>(null);
  const [product, setProduct] = useState<ProductAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [realtime, setRealtime] = useState<Realtime | null>(null);
  const [search, setSearch] = useState<SearchPerformance | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{
        success: true;
        data: { web: WebAnalytics | null; webError: string | null; product: ProductAnalytics };
      }>("/admin/web-analytics/overview", { params: { days } });
      setWeb(data.data.web);
      setWebError(data.data.webError);
      setProduct(data.data.product);
      setError(null);
    } catch (err) {
      setError(extractApiErrorMessage(err, "No se pudo cargar la analítica"));
    } finally {
      setLoading(false);
    }
  }, [days]);

  /** Búsquedas en Google. Va aparte: si falla, el resto del panel sigue. */
  const loadSearch = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ success: true; data: SearchPerformance | null; code?: string }>(
        "/admin/web-analytics/search",
        { params: { days } },
      );
      setSearch(data.data);
      setSearchError(
        data.data
          ? null
          : "Search Console todavía no está conectado: falta dar acceso a la cuenta de servicio en la propiedad.",
      );
    } catch (err) {
      setSearch(null);
      setSearchError(extractApiErrorMessage(err, "No se pudo leer Search Console"));
    }
  }, [days]);

  const loadRealtime = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ success: true; data: Realtime }>("/admin/web-analytics/realtime");
      setRealtime(data.data);
    } catch {
      setRealtime(null);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadSearch();
  }, [load, loadSearch]);

  useEffect(() => {
    void loadRealtime();
    const timer = window.setInterval(() => void loadRealtime(), 45_000);
    return () => window.clearInterval(timer);
  }, [loadRealtime]);

  const kpis = web
    ? [
        { label: "Visitantes", value: web.overview.users.toLocaleString("es-ES"), previous: web.previousOverview.users, current: web.overview.users },
        { label: "Nuevos", value: web.overview.newUsers.toLocaleString("es-ES"), previous: web.previousOverview.newUsers, current: web.overview.newUsers },
        { label: "Visitas", value: web.overview.sessions.toLocaleString("es-ES"), previous: web.previousOverview.sessions, current: web.overview.sessions },
        { label: "Páginas vistas", value: web.overview.pageViews.toLocaleString("es-ES"), previous: web.previousOverview.pageViews, current: web.overview.pageViews },
        { label: "Tiempo medio", value: formatSeconds(web.overview.averageEngagementSeconds), previous: web.previousOverview.averageEngagementSeconds, current: web.overview.averageEngagementSeconds },
        { label: "Visitas con interés", value: `${web.overview.engagedSessionRatePct} %`, previous: web.previousOverview.engagedSessionRatePct, current: web.overview.engagedSessionRatePct },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary-600" /> Analítica web
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Lo esencial de Google Analytics y de nuestros propios datos, sin dar vueltas por su panel.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setDays(r)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  days === r ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {r} días
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              void load();
              void loadSearch();
            }}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" /> Actualizar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${realtime && realtime.activeUsers > 0 ? "bg-emerald-400 animate-ping" : "bg-gray-300"}`} />
              <span className={`relative inline-flex rounded-full h-3 w-3 ${realtime && realtime.activeUsers > 0 ? "bg-emerald-500" : "bg-gray-400"}`} />
            </span>
            <div>
              <p className="text-sm text-gray-500">Ahora mismo en la web</p>
              <p className="text-2xl font-bold text-gray-900">
                {realtime ? realtime.activeUsers : "—"} <span className="text-base font-normal text-gray-500">personas</span>
              </p>
            </div>
          </div>
          {realtime && realtime.topPages.length > 0 && (
            <div className="text-sm text-gray-600 min-w-0">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Viendo ahora</p>
              {realtime.topPages.slice(0, 3).map((p) => (
                <p key={p.path} className="truncate">
                  {etiquetaDePagina(p.path)} · {p.users}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && !web && !product ? (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </p>
      ) : error ? (
        <p className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="w-4 h-4" /> {error}
        </p>
      ) : (
        <>
          {webError && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2 break-words">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {webError === "ANALYTICS_NOT_CONFIGURED"
                ? "Google Analytics no está conectado: falta la propiedad o el permiso de la cuenta de servicio. Abajo sigues viendo nuestros propios datos."
                : webError}
            </p>
          )}

          {web && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {kpis.map((kpi) => {
                  const v = variation(kpi.current, kpi.previous);
                  return (
                    <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-xs text-gray-500">{kpi.label}</p>
                      <p className="text-lg font-bold text-gray-900 mt-1">{kpi.value}</p>
                      <p className={`text-xs mt-1 ${v.className}`}>{v.text}</p>
                    </div>
                  );
                })}
              </div>

              <Section title="Visitas por día" icon={<Users className="w-5 h-5 text-primary-600" />}>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={web.byDay.map((d) => ({ ...d, label: shortDate(d.date) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value, name) => [
                          Number(value),
                          name === "users" ? "Visitantes" : name === "sessions" ? "Visitas" : "Páginas",
                        ]}
                      />
                      <Area type="monotone" dataKey="users" stroke="#0ea5e9" fill="#e0f2fe" name="users" />
                      <Area type="monotone" dataKey="pageViews" stroke="#94a3b8" fill="transparent" name="pageViews" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Section>

              <Section title="A qué horas entran" icon={<Clock className="w-5 h-5 text-primary-600" />}>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={web.byHour}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={1} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip formatter={(value) => [Number(value), "Visitantes"]} />
                      <Bar dataKey="users" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-400 mt-2">Hora de la propiedad de Analytics, sumando todos los días del rango.</p>
              </Section>

              <div className="grid lg:grid-cols-2 gap-4">
                <Section title="Páginas más visitadas" icon={<LayoutPanelTop className="w-5 h-5 text-primary-600" />}>
                  <div className="divide-y divide-gray-100">
                    {web.topPages.map((page) => (
                      <div key={page.path} className="py-2 flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0">
                          <a
                            href={urlDePagina(page.path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-primary-700 hover:underline truncate"
                            title={urlDePagina(page.path)}
                          >
                            {etiquetaDePagina(page.path)}
                          </a>
                          <span className="block text-xs text-gray-400 truncate">{page.title}</span>
                        </span>
                        <span className="text-right whitespace-nowrap">
                          <span className="block font-medium text-gray-900">{page.views}</span>
                          <span className="block text-xs text-gray-400">{formatSeconds(page.averageEngagementSeconds)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section title="De dónde llega la gente" icon={<Globe className="w-5 h-5 text-primary-600" />}>
                  <div className="divide-y divide-gray-100">
                    {web.sources.map((s, index) => (
                      <div key={`${s.source}-${s.medium}-${index}`} className="py-2 flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0">
                          <span className="block text-gray-900 truncate">{sourceLabel(s.source, s.medium)}</span>
                          {s.campaign && <span className="block text-xs text-gray-400 truncate">{s.campaign}</span>}
                        </span>
                        <span className="font-medium text-gray-900 whitespace-nowrap">{s.sessions}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <Section title="Dispositivos y países" icon={<Smartphone className="w-5 h-5 text-primary-600" />}>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      {web.devices.map((d) => (
                        <p key={d.device} className="flex justify-between py-1">
                          <span className="text-gray-600">{DEVICE_LABEL[d.device] ?? d.device}</span>
                          <span className="font-medium text-gray-900">{d.users}</span>
                        </p>
                      ))}
                    </div>
                    <div>
                      {web.countries.map((c) => (
                        <p key={c.country} className="flex justify-between py-1">
                          <span className="text-gray-600 truncate">{c.country}</span>
                          <span className="font-medium text-gray-900">{c.users}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                </Section>

                <Section title="Por dónde entran (primera página)" icon={<MousePointerClick className="w-5 h-5 text-primary-600" />}>
                  <div className="divide-y divide-gray-100">
                    {web.landingPages.map((p) => (
                      <p key={p.path} className="py-2 flex items-center justify-between gap-3 text-sm">
                        <a
                          href={urlDePagina(p.path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-700 hover:underline truncate"
                          title={urlDePagina(p.path)}
                        >
                          {etiquetaDePagina(p.path)}
                        </a>
                        <span className="font-medium text-gray-900 whitespace-nowrap">{p.sessions}</span>
                      </p>
                    ))}
                  </div>
                </Section>
              </div>
            </>
          )}

          {searchError && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2 break-words">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {searchError}
            </p>
          )}

          {search && (
            <>
              <p className="text-xs text-gray-500 -mb-2">
                Búsquedas en Google de la <strong>web de ventas</strong>. Las páginas de reserva de los clientes van
                aparte, más abajo, para que no tapen estas cifras.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Clics desde Google", value: search.totals.clicks, previous: search.previousTotals.clicks },
                  { label: "Veces que nos ven", value: search.totals.impressions, previous: search.previousTotals.impressions },
                  { label: "De cada 100 que nos ven, entran", value: search.totals.ctrPct, previous: search.previousTotals.ctrPct, suffix: " %" },
                  { label: "Posición media", value: search.totals.position, previous: search.previousTotals.position, invert: true },
                ].map((kpi) => {
                  // En la posición, bajar es MEJOR: salir el 3º gana al 9º.
                  const v = kpi.invert
                    ? variation(kpi.previous, kpi.value)
                    : variation(kpi.value, kpi.previous);
                  return (
                    <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-xs text-gray-500">{kpi.label}</p>
                      <p className="text-lg font-bold text-gray-900 mt-1">
                        {kpi.value.toLocaleString("es-ES")}
                        {kpi.suffix ?? ""}
                      </p>
                      <p className={`text-xs mt-1 ${v.className}`}>{v.text}</p>
                    </div>
                  );
                })}
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <Section title="Por qué palabras te encuentran" icon={<Search className="w-5 h-5 text-primary-600" />}>
                  {search.queries.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Todavía no hay palabras con datos suficientes. Google oculta las búsquedas que hace muy poca gente.
                    </p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {search.queries.map((q) => (
                        <div key={q.value} className="py-2 flex items-center justify-between gap-3 text-sm">
                          <span className="min-w-0">
                            <span className="block text-gray-900 truncate">{q.value}</span>
                            <span className="block text-xs text-gray-400">
                              posición {q.position} · {q.impressions.toLocaleString("es-ES")} veces vista
                            </span>
                          </span>
                          <span className="font-medium text-gray-900 whitespace-nowrap">{q.clicks} clics</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>

                <Section title="Qué páginas salen en Google" icon={<LayoutPanelTop className="w-5 h-5 text-primary-600" />}>
                  <div className="divide-y divide-gray-100">
                    {search.pages.map((p) => (
                      <div key={p.value} className="py-2 flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0">
                          <span className="block text-gray-900 truncate">
                            {p.value.replace(/^https?:\/\/[^/]+/, "") || "/"}
                          </span>
                          <span className="block text-xs text-gray-400">
                            posición {p.position} · {p.impressions.toLocaleString("es-ES")} veces vista
                          </span>
                        </span>
                        <span className="font-medium text-gray-900 whitespace-nowrap">{p.clicks} clics</span>
                      </div>
                    ))}
                  </div>
                </Section>
              </div>

              <Section title="Clics y apariciones en Google, día a día" icon={<Search className="w-5 h-5 text-primary-600" />}>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={search.byDay.map((d) => ({ ...d, label: shortDate(d.date) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value, name) => [Number(value), name === "clicks" ? "Clics" : "Veces que nos ven"]}
                      />
                      <Area type="monotone" dataKey="impressions" stroke="#c7d2fe" fill="#eef2ff" name="impressions" />
                      <Area type="monotone" dataKey="clicks" stroke="#4f46e5" fill="#e0e7ff" name="clicks" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Google va con retraso: el último día con datos es el {shortDate(search.until)}. Y oculta las búsquedas
                  de muy poca gente, así que la suma de las palabras nunca cuadra con el total.
                </p>
              </Section>

              <Section
                title="Páginas de reserva de los clientes (widget)"
                icon={<MousePointerClick className="w-5 h-5 text-primary-600" />}
              >
                <div className="flex flex-wrap gap-6 mb-3">
                  {[
                    { label: "Clics desde Google", value: search.widget.totals.clicks, previous: search.widget.previousTotals.clicks },
                    { label: "Veces que las ven", value: search.widget.totals.impressions, previous: search.widget.previousTotals.impressions },
                    { label: "Posición media", value: search.widget.totals.position, previous: search.widget.previousTotals.position, invert: true },
                  ].map((kpi) => {
                    const v = kpi.invert ? variation(kpi.previous, kpi.value) : variation(kpi.value, kpi.previous);
                    return (
                      <div key={kpi.label}>
                        <p className="text-xs text-gray-500">{kpi.label}</p>
                        <p className="text-lg font-bold text-gray-900">{kpi.value.toLocaleString("es-ES")}</p>
                        <p className={`text-xs ${v.className}`}>{v.text}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="divide-y divide-gray-100">
                  {search.widget.pages.map((p) => (
                    <div key={p.value} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0">
                        <span className="block text-gray-900 truncate">
                          {p.value.replace(/^https?:\/\/[^/]+\//, "") || "/"}
                        </span>
                        <span className="block text-xs text-gray-400">
                          posición {p.position} · {p.impressions.toLocaleString("es-ES")} veces vista
                        </span>
                      </span>
                      <span className="font-medium text-gray-900 whitespace-nowrap">{p.clicks} clics</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Son las páginas de reserva de cada negocio cliente: salen en Google por el nombre del negocio y se
                  dejan tal cual. Aquí solo para verlas, no se tocan.
                </p>
              </Section>
            </>
          )}

          {product && (
            <>
              <Section title="Registros y clientes (datos nuestros, sin depender de cookies)" icon={<UserPlus className="w-5 h-5 text-primary-600" />}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: "Registros", value: product.signups.total },
                    { label: "Con onboarding", value: product.signups.onboardingCompleted },
                    { label: "Con reservas", value: product.signups.withBookings },
                    { label: "Pagando", value: product.signups.payingNow },
                    { label: "En prueba", value: product.signups.stillTrialing },
                  ].map((item) => (
                    <div key={item.label} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                      <p className="text-xs text-gray-500">{item.label}</p>
                      <p className="text-lg font-bold text-gray-900 mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </Section>

              <div className="grid lg:grid-cols-2 gap-4">
                <Section
                  title="De dónde vienen los registros"
                  icon={<UserPlus className="w-5 h-5 text-primary-600" />}
                >
                  {product.signupOrigins.length === 0 ? (
                    <p className="text-sm text-gray-500">Sin registros en este periodo.</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {product.signupOrigins.map((o) => (
                        <p
                          key={o.origen}
                          className="py-2 flex items-center justify-between gap-3 text-sm"
                        >
                          <span
                            className={
                              o.origen === "widget"
                                ? "font-medium text-emerald-700"
                                : "text-gray-900"
                            }
                          >
                            {o.origen === "widget"
                              ? "Widget de un cliente"
                              : o.origen === "Sin datos"
                                ? "Sin datos (rechazó cookies o entró directo)"
                                : o.origen}
                          </span>
                          <span className="font-medium text-gray-900">{o.registros}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  {product.signupsFromWidget.length > 0 && (
                    <div className="mt-4 rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                      <p className="text-xs font-medium text-emerald-900 mb-1.5">
                        Clientes que te traen clientes
                      </p>
                      <div className="divide-y divide-emerald-100">
                        {product.signupsFromWidget.map((n) => (
                          <p
                            key={n.negocio}
                            className="py-1.5 flex items-center justify-between gap-3 text-sm text-emerald-900"
                          >
                            <span className="truncate">{n.negocio}</span>
                            <span className="font-semibold">{n.registros}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-3">
                    Sale de lo que trae la visita al registrarse. Quien rechaza las cookies de
                    marketing cuenta como "sin datos": el registro se guarda igual, pero no de dónde
                    vino.
                  </p>
                </Section>

                <Section title="Reservas y citas por canal" icon={<LayoutPanelTop className="w-5 h-5 text-primary-600" />}>
                  {product.channels.length === 0 ? (
                    <p className="text-sm text-gray-500">Sin reservas en este periodo.</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {product.channels.map((c) => (
                        <p key={c.channel} className="py-2 flex items-center justify-between gap-3 text-sm">
                          <span className="text-gray-900">{c.channel}</span>
                          <span className="font-medium text-gray-900">{c.bookings}</span>
                        </p>
                      ))}
                    </div>
                  )}
                </Section>

                <Section title="Uso del widget" icon={<MousePointerClick className="w-5 h-5 text-primary-600" />}>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: "Aperturas", value: product.widget.opens.toLocaleString("es-ES") },
                      { label: "Reservas", value: product.widget.bookings.toLocaleString("es-ES") },
                      { label: "Convierten", value: `${product.widget.conversionPct} %` },
                    ].map((item) => (
                      <div key={item.label} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                        <p className="text-xs text-gray-500">{item.label}</p>
                        <p className="text-lg font-bold text-gray-900 mt-0.5">{item.value}</p>
                      </div>
                    ))}
                  </div>
                  {product.widget.topOrganizations.length > 0 && (
                    <div className="mt-3 divide-y divide-gray-100">
                      {product.widget.topOrganizations.map((o) => (
                        <p key={o.organization} className="py-2 flex items-center justify-between gap-3 text-sm">
                          <span className="text-gray-900 truncate">{o.organization}</span>
                          <span className="font-medium text-gray-900">{o.opens}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-3">
                    Se cuenta en nuestro servidor cada vez que un cliente abre el widget en la web de un negocio: sin cookies ni
                    datos personales. Empieza a contar desde hoy.
                  </p>
                </Section>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
